import axios, {
  AxiosInstance as OriginAxiosInstance,
  AxiosInterceptorManager,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosHeaders as OriginAxiosHeaders,
  AxiosError,
} from 'axios';
import { getAccessToken, getRefreshToken } from '../storage/token';
import { logout } from '../utils/logIn';
import KStadiumAPI from './kstadiumAPI';

interface AxiosInstance extends OriginAxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<AxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse>;
  };
}

interface AxiosRequestConfig extends InternalAxiosRequestConfig {
  headers: AxiosHeaders;
}

type AxiosHeaders = OriginAxiosHeaders & {
  [key: string]: string | null;
};

const BridgeInstance: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_KSTADIUM_BRIDGE_BACKEND_URL}/api`,
  headers: {
    'Content-Type': `application/json`,
  },
});

const KStadiumInstance: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_KSTADIUM_BACKEND_URL}/kstadium/api/comm/`,
  headers: {
    'Content-Type': `application/json`,
  },
});

const isAxiosError = (error: any): error is AxiosError => {
  return error?.isAxiosError;
};

export const validateAuthError = async (error: AxiosError) => {
  // NOTE: type 확정하기
  const res = error.response?.data as any;
  const url = error.config?.url;

  if (res) {
    const otpError = `statusCode` in res;
    if (!otpError) {
      if (url === `/member/login/refresh`) {
        logout();
      } else {
        // NOTE : access token error
        const originalConfig = error.config as AxiosRequestConfig;

        try {
          if (url === `/member/login/2fa/bridge`) {
            throw new Error(`jwt expire error`);
          }

          const { accessToken } = await KStadiumAPI.requestRefreshToken();

          originalConfig.headers[`X-AUTH-TOKEN`] = accessToken;
          return await axios(originalConfig);
        } catch (data) {
          logout();
        }
      }
    }
  }

  throw error;
};

const requestIntercept = (config: AxiosRequestConfig): AxiosRequestConfig => {
  const { url } = config;

  if (url === `/member/login/login`) return config;

  config.headers[`X-AUTH-TOKEN`] = getAccessToken(
    // NOTE : newContract API 리팩토링 후 disposable token 사용으로 추가하기
    url === `/member/login/2fa/bridge` ? `disposable` : `default`,
  );

  if (url !== `/member/login/refresh`) return config;

  config.headers[`X-AUTH-REFRESH-TOKEN`] = getRefreshToken();
  return config;
};

const errorIntercept = (error: unknown) => {
  if (isAxiosError(error)) {
    const status = error.response?.status;

    if (status === 401) {
      return validateAuthError(error);
    }

    throw error;
  }

  // NOTE: alert 창으로 에러 보여주기
  throw new Error(`Sorry error occurred`);
};

BridgeInstance.interceptors.response.use(null, errorIntercept);
KStadiumInstance.interceptors.response.use(null, errorIntercept);
KStadiumInstance.interceptors.request.use(requestIntercept);

export { BridgeInstance, KStadiumInstance };
